import dayjs from '../../utils/dayjs';
import __helpers from 'helpers/index';
import { useState, useEffect, useCallback, useRef } from 'react';

import { BlockStack, Box, Button, DataTable, FooterHelp, Link, Page, Text, Tooltip, useBreakpoints } from '@shopify/polaris';
import { Helmet } from 'react-helmet-async';
import DateRangePicker from 'components/dateRangePicker';
import { PageDownIcon } from '@shopify/polaris-icons';
import { useDownloadStaffRevenue, useGetFinalStaffRevenue } from 'queries/analytics.query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChessKing, faMedal } from '@fortawesome/free-solid-svg-icons';

type StaffRevenue = {
  user_id: string;
  display_name: string;
  user_phonenumber: string;
  total_order: number;
  total_fare: number;
  total_passenger: number;
  total_good: number;
  rank: number;
  is_top: boolean;
};

export default function StaffReport() {
  const [defaultDate, setDefaultDate] = useState<any>({
    startDate: dayjs(dayjs().add(-7, 'day')).format('YYYY-MM-DD 00:00:00'),
    endDate: dayjs(new Date()).format('YYYY-MM-DD 23:59:59'),
  });

  /** Nhân viên */
  const { data: dataStaffRevenue, isLoading: loadingDataStaffRevenue } = useGetFinalStaffRevenue(defaultDate);
  const [entities, setEntities] = useState<StaffRevenue[]>([]);
  const [tableData, setTableData] = useState([]);
  const [totalOrder, setTotalOrder] = useState<number>(0);
  const [totalFare, setTotalFare] = useState<number>(0);
  const [totalPassenger, setTotalPassenger] = useState<number>(0);
  const [totalGood, setTotalGood] = useState<number>(0);

  useEffect(() => {
    setEntities(dataStaffRevenue || []);
  }, [dataStaffRevenue]);

  /** Gán một giao dịch nào đó vào vé nào đó */
  const currentStaffForDownload = useRef(null);
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadStaffRevenue();
  const generateDownloadLink = useCallback(async () => {
    try {
      const blob = await downloadExcelThisFilter({ ...defaultDate, user_id: currentStaffForDownload.current });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `staff_revenue-${currentStaffForDownload.current}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {}
  }, [defaultDate, currentStaffForDownload.current]);

  useEffect(() => {
    if (!__helpers.isArray(entities)) return;
    if (__helpers.isEmpty(entities)) {
      setTotalOrder(0);
      setTotalFare(0);
      setTotalPassenger(0);
      setTotalGood(0);

      setTableData([]);
    } else {
      let totalResult = [];
      for (let ele of entities) {
        totalResult.push([
          ele.rank,
          <Link onClick={() => (window.location.href = `/users/view/${ele.user_id}`)}>
            {ele.rank === 1 ? <FontAwesomeIcon icon={faChessKing} /> : null}
            {ele.rank === 2 ? <FontAwesomeIcon icon={faMedal} /> : null}
            &nbsp;
            {`${ele.display_name} (${ele.user_phonenumber || 'no_phone'})`}
          </Link>,
          __helpers.formatNumberWithCommas(ele.total_order),
          __helpers.formatNumberWithCommas(ele.total_fare),
          __helpers.formatNumberWithCommas(ele.total_good),
          __helpers.formatNumberWithCommas(ele.total_passenger),
          <Tooltip content="Xuất báo cáo đơn hàng của nhân viên này">
            <Button
              icon={PageDownIcon}
              variant="plain"
              onClick={() => {
                currentStaffForDownload.current = ele.user_id;
                generateDownloadLink();
              }}
            ></Button>
          </Tooltip>,
        ]);
      }
      const totalOrder = entities.reduce((sum, item) => sum + item.total_order, 0);
      const totalFare = entities.reduce((sum, item) => sum + item.total_fare, 0);
      const totalPassenger = entities.reduce((sum, item) => sum + item.total_passenger, 0);
      const totalGood = entities.reduce((sum, item) => sum + item.total_good, 0);
      setTotalOrder(totalOrder);
      setTotalFare(totalFare);
      setTotalPassenger(totalPassenger);
      setTotalGood(totalGood);
      setTableData(totalResult);
    }
  }, [entities]);

  /**
   * Date time filling ...
   */

  const dateChangeCallback = useCallback((_e) => {
    let { since, until } = _e.period;
    let FNS = {
      startDate: '',
      endDate: '',
    };
    if (since) {
      FNS.startDate = new Date(dayjs(since).format('YYYY-MM-DD 00:00:00')).toISOString();
    }
    if (until) {
      FNS.endDate = new Date(dayjs(until).format('YYYY-MM-DD 23:59:59')).toISOString();
    }
    setDefaultDate(FNS);
  }, []);

  let { lgDown } = useBreakpoints();
  const fixedFirstColumns = lgDown ? 1 : 0;

  const handleSort = useCallback(
    (index, direction) => {
      let _direction = direction === 'descending' ? 'desc' : 'asc';
      switch (index) {
        case 2:
          setEntities(entities.slice().sort((a, b) => (_direction === 'asc' ? a.total_order - b.total_order : b.total_order - a.total_order)));
          break;
        case 3:
          setEntities(entities.slice().sort((a, b) => (_direction === 'asc' ? a.total_fare - b.total_fare : b.total_fare - a.total_fare)));
          break;
        case 4:
          setEntities(entities.slice().sort((a, b) => (_direction === 'asc' ? a.total_good - b.total_good : b.total_good - a.total_good)));
          break;
        case 5:
          setEntities(
            entities.slice().sort((a, b) => (_direction === 'asc' ? a.total_passenger - b.total_passenger : b.total_passenger - a.total_passenger))
          );
          break;
      }
    },
    [entities]
  );

  return (
    <>
      <Helmet>
        <title>Báo cáo nhân viên</title>
      </Helmet>
      <Page title="Báo cáo nhân viên" subtitle={`Khoảng thời gian: ${defaultDate.startDate} - ${defaultDate.endDate}`}>
        <BlockStack gap="400">
          <Box>
            <Text as="p">Doanh số bán vé</Text>
            <br />
            <DateRangePicker onChange={dateChangeCallback} />
            <br />
            {loadingDataStaffRevenue ? (
              <Text as="p">Đang tải dữ liệu...</Text>
            ) : (
              <DataTable
                sortable={[false, false, true, true, true, true, false]}
                initialSortColumnIndex={1}
                defaultSortDirection="descending"
                onSort={handleSort}
                hideScrollIndicator
                fixedFirstColumns={fixedFirstColumns}
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                headings={['No.', 'Nhân viên', 'Tổng đơn', 'Tổng tiền vé', 'Tổng hàng', 'Tổng khách', 'Tools']}
                rows={tableData}
                totals={[
                  '',
                  '',
                  __helpers.formatNumberWithCommas(totalOrder),
                  __helpers.formatNumberWithCommas(totalFare),
                  __helpers.formatNumberWithCommas(totalGood),
                  __helpers.formatNumberWithCommas(totalPassenger),
                  'Tools',
                ]}
                showTotalsInFooter
                totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
                hasZebraStripingOnData
                increasedTableDensity
              />
            )}
          </Box>
          <FooterHelp align="start">
            <Text as="p" variant="bodySm" tone="subdued">
              * Thống kê ở đây được lấy từ phiếu quyết toán với tài xế. Thông tin doanh thu sẽ được ghi nhận sau khi tài xế chốt lệnh.
            </Text>
          </FooterHelp>
        </BlockStack>
      </Page>
    </>
  );
}

